import React, { useState } from 'react';
import { FaCircle, FaShieldAlt, FaLock, FaChevronDown, FaChevronUp, FaWifi, FaBolt, FaMapMarkedAlt, FaStream, FaChartBar, FaUserShield } from 'react-icons/fa';

const TechnologySection: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowScrollIndicator(target.scrollTop > 20);
  };
  const technologies = [
    {
      number: 1,
      icon: FaWifi,
      title: 'Proximity Engine',
      description: 'A lightweight, hybrid proximity system that detects nearby users through multiple signals without relying on any single technology.',
      features: [
        'Works indoors and outdoors',
        'On campuses',
        'In crowded venues',
        'Minimal battery impact'
      ],
      note: 'We don\'t share specific algorithms — just the outcome: ultra-fast, reliable proximity detection.'
    },
    {
      number: 2,
      icon: FaBolt,
      title: 'Real-Time Interaction Layer',
      description: 'The platform continuously merges real-world movement with live digital interactions.',
      features: [
        'Instant collections',
        'Live attributes',
        'Dynamic proximity chats',
        'Real-time venue engagement'
      ],
      note: 'All optimized for speed, privacy, and seamless user experience.'
    },
    {
      number: 3,
      icon: FaMapMarkedAlt,
      title: 'Intelligent Geofencing',
      description: 'CollectedAF supports adaptive geofences that allow:',
      features: [
        'Automatic Popin creation',
        'Live group chats',
        'Micro-communities inside venues',
        'Dynamic location-based behavior without manual setup'
      ],
      note: 'Advanced geofencing that adapts to your movement, creating seamless location-based experiences.'
    },
    {
      number: 4,
      icon: FaStream,
      title: 'Unified User Pipeline',
      description: 'We combine multiple data inputs into a single, clean view of who\'s nearby — filtered, deduplicated, and updated intelligently.',
      features: [
        'No duplicates',
        'No flicker',
        'No "jumping"',
        'Smooth and accurate user representation'
      ],
      note: 'It\'s the reason our UI feels alive rather than glitchy.'
    },
    {
      number: 5,
      icon: FaChartBar,
      title: 'Venue Intelligence System',
      description: 'Behind the scenes, CollectedAF builds real-time insights for venues:',
      features: [
        'Foot-traffic patterns',
        'Engagement metrics',
        'Micro-community behavior',
        'Live presence heat',
        'Offer performance analytics'
      ],
      note: 'It\'s built to help businesses understand their real audience in real time — something traditional social media can\'t do.'
    },
    {
      number: 6,
      icon: FaUserShield,
      title: 'Privacy-First Architecture',
      description: 'CollectedAF never exposes raw location. We use proximity-safe, privacy-preserving interaction models that allow users to connect without revealing sensitive information.',
      features: [],
      note: 'Your privacy is built into the foundation of our platform, not added as an afterthought.'
    }
  ];

  const defensibility = [
    'Multi-signal proximity',
    'Low latency pipelines',
    'Live Intelligent geofencing',
    'Venue analytics',
    'Gamified collection mechanics',
  ];

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        {/* Background Giant C */}
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[30rem] opacity-25 pointer-events-none"
          style={{ 
            fontFamily: '"Anton", sans-serif',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 45%, #fbbf24 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.35))'
          }}
        >
          C
        </div>
        
        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="colorful-ring bg-BLACK/96 backdrop-blur-2xl rounded-3xl p-6 lg:p-12 w-full h-auto lg:h-full transition-colors shadow-2xl flex flex-col">
            <h2 className="text-3xl lg:text-4xl font-bold text-WHITE mb-3 text-center">THE TECHNOLOGY POWERING COLLECTEDAF</h2>
            <div className="bg-WHITE/10 backdrop-blur-xl rounded-2xl px-6 py-3 mb-6 mx-auto w-fit border border-WHITE/15 shadow-lg">
              <p
                className="text-lg font-bold text-center leading-relaxed"
                style={{
                  background: 'linear-gradient(90deg, #000000 0%, #0066cc 50%, #000000 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Built to connect people and places in real time — instantly, privately, and at scale.
              </p>
            </div>
                
            {/* Scroll Indicator */}
            <div className={`absolute top-32 left-0 right-0 h-12 bg-gradient-to-b from-WHITE/20 to-transparent pointer-events-none transition-opacity duration-300 z-10 ${showScrollIndicator ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex justify-center pt-2">
                <FaChevronUp className="text-WHITE/60 animate-bounce" size={24} />
              </div>
            </div>
                
            {/* Technology Accordions - Scrollable */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-6 pr-2" onScroll={handleScroll}>
              {technologies.map((tech, index) => {
                const Icon = tech.icon;
                return (
                  <div key={index} className="bg-WHITE/5 rounded-xl border border-WHITE/10 hover:border-WHITE/30 transition-colors">
                    {/* Accordion Header */}
                    <button
                      onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-WHITE/20 backdrop-blur-md flex items-center justify-center border-2 border-WHITE/25">
                        <Icon className="text-WHITE" size={20} />
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-WHITE/20 border border-WHITE/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-WHITE text-lg font-bold">{tech.number}</span>
                        </div>
                        <h4 className="text-WHITE text-lg font-bold">{tech.title}</h4>
                      </div>
                      {openAccordion === index ? (
                        <FaChevronUp className="text-WHITE/60" size={20} />
                      ) : (
                        <FaChevronDown className="text-WHITE/50" size={20} />
                      )}
                    </button>
                  
                    {/* Accordion Content */}
                    {openAccordion === index && (
                      <div className="px-4 pb-4 pt-0">
                        <p className="text-WHITE/70 text-sm mb-3 leading-relaxed">{tech.description}</p>
                        
                        {tech.features.length > 0 && (
                          <ul className="space-y-1 mb-3">
                            {tech.features.map((feature, idx) => (
                              <li key={idx} className="text-WHITE/60 text-sm flex items-center gap-2">
                                <FaCircle size={6} className="text-WHITE/50" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        )}
                        
                        {tech.note && (
                          <p className="text-WHITE/50 text-xs italic leading-relaxed">{tech.note}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Defensibility Section */}
            <div className="bg-WHITE/10 backdrop-blur-xl rounded-xl p-6 border border-WHITE/15 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-WHITE/20 backdrop-blur-md flex items-center justify-center border-2 border-WHITE/25">
                  <FaLock className="text-WHITE" size={24} />
                </div>
                <h3 className="text-WHITE text-2xl font-bold">Defensibility</h3>
              </div>
              
              <p className="text-WHITE/90 text-base mb-4 leading-relaxed">
                What makes our tech hard to copy is not a single feature — it's the <span className="text-WHITE font-bold">system</span>:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-4">
                {defensibility.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-WHITE/80 text-sm">
                    <FaShieldAlt className="text-WHITE/60 flex-shrink-0" size={14} />
                    {item}
                  </div>
                ))}
              </div>
              
              <div className="bg-WHITE/10 backdrop-blur-xl rounded-2xl px-6 py-4 border border-WHITE/15 shadow-lg">
                <p
                  className="text-base font-bold text-center leading-relaxed"
                  style={{
                    background: 'linear-gradient(90deg, #000000 0%, #0066cc 50%, #000000 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Anyone can copy one piece. No one can replicate the ecosystem.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologySection;
